import React from 'react';
import * as UI from '../components';

const common = UI.commonStyles || {
  spacing: {
    xs: '0.5rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  colors: {
    primary: '#0d6efd',
    success: '#198754',
    info: '#0dcaf0',
    light: '#f8f9fa',
    muted: '#6c757d',
    dark: '#212529',
  },
};

const Card =
  UI.Card ||
  function FallbackCard({ children, title, icon }) {
    return (
      <div style={{ border: '1px solid #eee', padding: '1rem', marginBottom: '1rem', background: '#fff' }}>
        {(title || icon) && (
          <div style={{ marginBottom: '0.5rem', fontWeight: 600 }}>
            {icon} {title}
          </div>
        )}
        {children}
      </div>
    );
  };

const SectionHeader =
  UI.SectionHeader ||
  function FallbackHeader({ icon, title, subtitle }) {
    return (
      <div style={{ marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
        <div style={{ fontSize: '1rem', fontWeight: 600 }}>
          {icon} {title}
        </div>
        {subtitle && <div style={{ fontSize: '0.75rem', color: '#666' }}>{subtitle}</div>}
      </div>
    );
  };

const LoadingState =
  UI.LoadingState ||
  function FallbackLoading({ message }) {
    return <div style={{ padding: '1rem' }}>{message || 'Loading...'}</div>;
  };

const EmptyState =
  UI.EmptyState ||
  function FallbackEmpty({ title, description }) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#777' }}>
        <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{title}</div>
        {description && <div style={{ fontSize: '0.75rem' }}>{description}</div>}
      </div>
    );
  };

const Badge =
  UI.Badge ||
  function FallbackBadge({ label }) {
    return (
      <span style={{ background: '#0d6efd', color: '#fff', padding: '0.25rem 0.5rem', borderRadius: '12px', fontSize: '0.65rem' }}>
        {label}
      </span>
    );
  };

const NotificationsTab = ({ loading, notifications, userCode }) => {
  if (loading) {
    return <LoadingState message="Loading notifications..." />;
  }

  if (!userCode) {
    return (
      <div style={{ padding: common.spacing.lg }}>
        <EmptyState
          icon="🔔"
          title="No User Code"
          description="Enter a user code in the controls above and click 'Get Notifications'"
        />
      </div>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <div style={{ padding: common.spacing.lg }}>
        <SectionHeader icon="🔔" title="Notifications" subtitle={`User Code: ${userCode}`} />
        <EmptyState
          icon="📭"
          title="No Notifications"
          description="No notification history found for this user"
        />
      </div>
    );
  }

  return (
    <div style={{ padding: common.spacing.lg }}>
      <SectionHeader icon="🔔" title="Notifications" subtitle={`User Code: ${userCode}`} />

      {notifications.map((group, groupIndex) => (
        <Card
          key={groupIndex}
          title={`📬 Notified: ${new Date(group.notified_at).toLocaleString()}`}
          icon="⏰"
        >
          {group.rebates && group.rebates.length > 0 ? (
            <div style={{ display: 'grid', gap: common.spacing.md }}>
              {group.rebates.map((rebate, rebateIndex) => (
                <div
                  key={rebateIndex}
                  style={{
                    padding: common.spacing.md,
                    backgroundColor: common.colors.light,
                    borderRadius: '6px',
                    borderLeft: `4px solid ${common.colors.primary}`,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: common.spacing.md }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: common.spacing.xs }}>
                        {rebate.event}
                      </div>
                      <div style={{ color: common.colors.muted, fontSize: '12px', marginBottom: common.spacing.sm }}>
                        Sold: {rebate.sold_date} | Purchased: {rebate.purchase_date}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: common.spacing.md }}>
                        <div>
                          <div style={{ color: common.colors.muted, fontSize: '11px' }}>Trust Rebate</div>
                          <div style={{ fontWeight: 600, color: common.colors.success }}>
                            £{rebate.trust_rebate?.toFixed(2) || '0.00'}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: common.colors.muted, fontSize: '11px' }}>Referral Rebate</div>
                          <div style={{ fontWeight: 600, color: common.colors.info }}>
                            £{rebate.referral_rebate?.toFixed(2) || '0.00'}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: common.colors.muted, fontSize: '11px' }}>Total</div>
                          <div style={{ fontWeight: 600, color: common.colors.primary, fontSize: '16px' }}>
                            £{rebate.total_rebate?.toFixed(2) || '0.00'}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: common.colors.muted, fontSize: '11px' }}>Tickets</div>
                          <div style={{ fontWeight: 600 }}>
                            {rebate.number_of_tickets_sold || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                    <Badge label="📌" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: common.colors.muted, textAlign: 'center', padding: common.spacing.md }}>
              No rebates in this notification
            </div>
          )}
        </Card>
      ))}

      <Card title="📊 Summary" icon="📈">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: common.spacing.md }}>
          <div>
            <div style={{ color: common.colors.muted, fontSize: '12px', marginBottom: common.spacing.xs }}>
              Total Notifications
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: common.colors.primary }}>
              {notifications.length}
            </div>
          </div>
          <div>
            <div style={{ color: common.colors.muted, fontSize: '12px', marginBottom: common.spacing.xs }}>
              Total Rebates
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: common.colors.primary }}>
              {notifications.reduce((sum, g) => sum + (g.rebates?.length || 0), 0)}
            </div>
          </div>
          <div>
            <div style={{ color: common.colors.muted, fontSize: '12px', marginBottom: common.spacing.xs }}>
              Last Notification
            </div>
            <div style={{ fontSize: '13px', color: common.colors.dark }}>
              {notifications[0]?.notified_at
                ? new Date(notifications[0].notified_at).toLocaleDateString()
                : 'N/A'}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default NotificationsTab;
